import { z } from 'zod';

const BaseUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  email: z.string().email(),
  locale: z.string(),
  timezone: z.string(),
  force_password_change: z.boolean(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});

export const EmployeeSchema = BaseUserSchema.extend({
  user_type: z.literal('employee'),
  is_super_employee: z.boolean().optional(),
  permissions: z.array(z.string()).optional(),
  has_2fa_enabled: z.boolean().optional(),
});

export const CustomerSchema = BaseUserSchema.extend({
  user_type: z.literal('customer'),
  permissions: z.array(z.string()).optional(),
  roles: z.array(z.string()).optional(),
  has_2fa_enabled: z.boolean().optional(),
});

export const UserSchema = z.discriminatedUnion('user_type', [EmployeeSchema, CustomerSchema]);

const LoginEmployeeSchema = BaseUserSchema.extend({
  is_super_employee: z.boolean().optional(),
  permissions: z.array(z.string()).optional(),
  has_2fa_enabled: z.boolean().optional(),
});

const LoginCustomerSchema = BaseUserSchema.extend({
  permissions: z.array(z.string()).optional(),
  roles: z.array(z.string()).optional(),
});

export const LoginCredentialsSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const LoginResponseSchema = z.object({
  token: z.string(),
  user_type: z.enum(['employee', 'customer']),
  requires_2fa: z.boolean(),
  user: z.union([LoginEmployeeSchema, LoginCustomerSchema]).optional(),
  requires_company_selection: z.boolean().optional(),
});

export const UpdateLocaleResponseSchema = z.object({
  locale: z.string(),
});

export const UpdateTimezoneResponseSchema = z.object({
  timezone: z.string(),
});

export const UpdatePasswordResponseSchema = z.void();

export const UpdateEmailResponseSchema = z.object({
  message: z.string(),
  newEmail: z.string().email(),
});

export const ConfirmSensitiveResponseSchema = z.object({
  confirmed_at: z.string().datetime(),
});

export type BaseUser = z.infer<typeof BaseUserSchema>;
export type Employee = z.infer<typeof EmployeeSchema>;
export type Customer = z.infer<typeof CustomerSchema>;
export type User = z.infer<typeof UserSchema>;
export type LoginCredentials = z.infer<typeof LoginCredentialsSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
